import type { ReactNode } from 'react';
import React, { useMemo } from 'react';
import { z } from 'zod';
import {
  Button,
  Flashy,
  Form,
  Input,
  Layout,
  LayoutItem,
  List,
  ListItem,
} from '@axiom/ui';
import InfiniteScroll from 'react-infinite-scroll-component';

type WithId = { id: string };

export type AdminModalSearchListRow = {
  content: ReactNode;
  inactive?: boolean;
  trailing?: ReactNode;
};

export type AdminModalSearchListPagination = {
  hasMore: boolean;
  onLoadMore: () => void;
};

export type AdminModalSearchListAddControl =
  | { kind: 'button'; label: string; name: string; onAdd: () => void }
  | { kind: 'link'; label: string; name: string; href: string }
  | { kind: 'none' };

const NO_ADD_CONTROL: AdminModalSearchListAddControl = { kind: 'none' };

export type AdminModalSearchListProps<T extends WithId> = {
  name: string;
  searchName: string;
  searchPlaceholder?: string;
  onSearchChange: (value: string) => void;
  items: T[];
  selectedId: T['id'] | null;
  onSelect: (item: T) => void;
  renderRow: (item: T) => AdminModalSearchListRow;
  filterSlot?: ReactNode;
  addControl?: AdminModalSearchListAddControl;
  pagination?: AdminModalSearchListPagination;
};

export const AdminModalSearchList = <T extends WithId>({
  name,
  searchName,
  searchPlaceholder,
  onSearchChange,
  items,
  selectedId,
  onSelect,
  renderRow,
  filterSlot,
  addControl = NO_ADD_CONTROL,
  pagination,
}: AdminModalSearchListProps<T>) => {
  const searchSchema = useMemo(
    () => z.object({ [searchName]: z.string().nullish() }),
    [searchName]
  );
  const searchInitialValues = useMemo(
    () => ({ [searchName]: '' }),
    [searchName]
  );

  const rows = (
    <List name="RESULTS_LIST" condensed>
      {items.map(item => {
        const { content, inactive, trailing } = renderRow(item);
        const body = inactive ? (
          <Flashy color="textSubtle">{content}</Flashy>
        ) : (
          content
        );
        return (
          <ListItem
            key={item.id}
            name="RESULT_ITEM"
            selected={selectedId === item.id}
            onClick={() => {
              onSelect(item);
            }}
          >
            {trailing ? (
              <Layout position="space-between middle" horizontalGutter="8px">
                <LayoutItem fluid>{body}</LayoutItem>
                <LayoutItem>{trailing}</LayoutItem>
              </Layout>
            ) : (
              body
            )}
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <Layout name={name} direction="vertical" verticalGutter="8px">
      <LayoutItem>
        <Form
          name={`${searchName}_FORM`}
          schema={searchSchema}
          initialValues={searchInitialValues}
          submitOnChange
          onSubmit={(_changedData, _actions, values) => {
            const value = values[searchName];
            onSearchChange(value == null ? '' : String(value));
          }}
        >
          {() => (
            <Input
              name={searchName}
              icon="search"
              placeholder={searchPlaceholder}
            />
          )}
        </Form>
      </LayoutItem>
      {filterSlot ? <LayoutItem>{filterSlot}</LayoutItem> : null}
      {addControl.kind === 'button' ? (
        <LayoutItem>
          <Button
            name={addControl.name}
            icon="plus"
            pattern="secondary"
            variation="minimal"
            onClick={() => {
              addControl.onAdd();
            }}
          >
            {addControl.label}
          </Button>
        </LayoutItem>
      ) : null}
      {addControl.kind === 'link' ? (
        <LayoutItem>
          <Button
            name={addControl.name}
            pattern="secondary"
            variation="minimal"
            toTab={addControl.href}
          >
            {addControl.label}
          </Button>
        </LayoutItem>
      ) : null}
      <LayoutItem>
        <InfiniteScroll
          dataLength={items.length}
          next={() => {
            pagination?.onLoadMore();
          }}
          hasMore={pagination?.hasMore ?? false}
          loader={null}
          height="400px"
        >
          {rows}
        </InfiniteScroll>
      </LayoutItem>
    </Layout>
  );
};
